import type { WriteWith } from "@aybuben/client/device/writing.ts";
import { Keyboard as KeyboardIcon, LayoutGrid } from "lucide-react";
import type { ReactNode } from "react";

import { useStrings } from "../../i18n/index.tsx";
import { COL, FOCUS } from "../../theme.ts";
import { useWriting } from "./context.tsx";

interface WriteSwitchProps {
	/** Called after the choice changes, so the view can drop a half-made answer. */
	onSwitch: () => void;
	disabled: boolean;
}

/** Tiles or keyboard, on every writing step. The choice is the device's, and sticks. */
export function WriteSwitch({ onSwitch, disabled }: WriteSwitchProps) {
	const t = useStrings();
	const w = useWriting();
	const pick = (next: WriteWith) => {
		if (disabled || next === w.write) return;
		w.setWrite(next);
		onSwitch();
	};
	const opt = (id: WriteWith, label: string, icon: ReactNode) => {
		const on = w.write === id;
		return (
			<button
				type="button"
				onClick={() => pick(id)}
				aria-pressed={on}
				disabled={disabled}
				className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold ${FOCUS}`}
				style={{
					background: on ? COL.card : "transparent",
					color: on ? COL.ink : COL.mute,
					boxShadow: on ? `0 1px 0 ${COL.line}` : "none",
				}}
			>
				{icon}
				{label}
			</button>
		);
	};
	return (
		<div
			role="group"
			aria-label={t("write.with")}
			className="inline-flex rounded-full p-1"
			style={{ background: COL.keys, opacity: disabled ? 0.6 : 1 }}
		>
			{opt("tiles", t("write.tiles"), <LayoutGrid size={16} aria-hidden="true" />)}
			{opt("keys", t("write.keyboard"), <KeyboardIcon size={16} aria-hidden="true" />)}
		</div>
	);
}
